import { useState } from 'react'

import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  EMPTY_OBJECT,
} from '../../../../constants/common'
import { toggleItem } from '../../../../utils/common'
import { GRETA_SKELETON_DELAY_MS } from '../../GretaAIChat.constants'
import {
  useGretaAIChatContext,
  useGretaAIChatDelayedFlag,
} from '../../GretaAIChat.hooks'
import {
  getIsGretaAIChatIdle,
  getIsGretaAIChatLoading,
  getIsGretaAIChatRetryableStatus,
} from '../../GretaAIChat.utils'
import { getHasGretaAIChatProjects } from '../../GretaAIChat.utils.projects'
import { getGretaAIChatChatListFlags } from '../GretaAIChatChatList.utils'
import { GretaAIChatChatListProjectsEmpty } from '../GretaAIChatChatListProjectsEmpty'
import { GretaAIChatChatListProjectsError } from '../GretaAIChatChatListProjectsError'
import { GretaAIChatChatListProjectsHeader } from '../GretaAIChatChatListProjectsHeader'
import { GretaAIChatChatListProjectsList } from '../GretaAIChatChatListProjectsList'
import { GretaAIChatChatListProjectsMore } from '../GretaAIChatChatListProjectsMore'
import { GretaAIChatChatListProjectsSkeleton } from '../GretaAIChatChatListProjectsSkeleton'

import {
  getGretaAIChatVisibleProjects,
  getHasGretaAIChatHiddenProjects,
  getHasGretaAIChatProjectsBeyondLimit,
} from './GretaAIChatChatListProjects.utils'

import * as styles from './GretaAIChatChatListProjects.module.css'

function GretaAIChatChatListProjects({
  activeChatId,
  chatStatusById = EMPTY_OBJECT,
  locale,
  now,
  withRowActions = true,
  onSelectChat = EMPTY_FUNCTION,
}) {
  const { actions, projects: projectsData } = useGretaAIChatContext()
  const { loadProjects, projects, projectsStatus } = projectsData
  const { onOpenProjectsPage } = actions

  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isAllShown, setIsAllShown] = useState(false)
  const [expandedProjectIds, setExpandedProjectIds] = useState(EMPTY_LIST)
  const [fullProjectIds, setFullProjectIds] = useState(EMPTY_LIST)

  const projectsLoading = getIsGretaAIChatLoading(projectsStatus)

  const { isEmpty, isError, isLoading, isRows } = getGretaAIChatChatListFlags({
    hasChats: getHasGretaAIChatProjects(projects),
    hasError: !projectsLoading && !getIsGretaAIChatIdle(projectsStatus),
    isLoading: projectsLoading,
  })
  const isRetryable = getIsGretaAIChatRetryableStatus(projectsStatus)

  const isSkeletonShown = useGretaAIChatDelayedFlag(
    isLoading,
    GRETA_SKELETON_DELAY_MS
  )

  const visibleProjects = getGretaAIChatVisibleProjects({
    activeChatId,
    isAllShown,
    projects,
  })
  const hiddenProjectsCount = projects.length - visibleProjects.length
  const isMoreShown =
    getHasGretaAIChatProjectsBeyondLimit(projects) &&
    (isAllShown || getHasGretaAIChatHiddenProjects(hiddenProjectsCount))

  function handleToggle() {
    setIsCollapsed(current => !current)
  }

  function handleToggleAllProjects() {
    setIsAllShown(current => !current)
  }

  function handleToggleProject(projectId) {
    setExpandedProjectIds(current => toggleItem(projectId, current))
    setFullProjectIds(current => current.filter(id => id !== projectId))
  }

  function handleShowAllChats(projectId) {
    setFullProjectIds(current => [...current, projectId])
  }

  return (
    <section className={styles.projects}>
      <GretaAIChatChatListProjectsHeader
        isCollapsed={isCollapsed}
        onOpenProjectsPage={onOpenProjectsPage}
        onToggle={handleToggle}
      />

      {isCollapsed ? null : (
        <>
          {isSkeletonShown ? <GretaAIChatChatListProjectsSkeleton /> : null}

          {isError ? (
            <GretaAIChatChatListProjectsError
              onRetry={isRetryable ? loadProjects : null}
            />
          ) : null}

          {isEmpty ? <GretaAIChatChatListProjectsEmpty /> : null}

          {isRows ? (
            <GretaAIChatChatListProjectsList
              activeChatId={activeChatId}
              chatStatusById={chatStatusById}
              expandedProjectIds={expandedProjectIds}
              fullProjectIds={fullProjectIds}
              locale={locale}
              now={now}
              projects={visibleProjects}
              withRowActions={withRowActions}
              onSelectChat={onSelectChat}
              onShowAllChats={handleShowAllChats}
              onToggleProject={handleToggleProject}
            />
          ) : null}

          {isRows && isMoreShown ? (
            <GretaAIChatChatListProjectsMore
              hiddenCount={hiddenProjectsCount}
              isAllShown={isAllShown}
              onToggle={handleToggleAllProjects}
            />
          ) : null}
        </>
      )}
    </section>
  )
}

export default GretaAIChatChatListProjects
